import { useRef, useEffect } from 'react';
import { useCallbackOne } from 'use-memo-one';
import {
	BridgeContextObject,
	type BridgeContextInput,
	type ChangeListener,
} from './BridgeContextObject';
import type { BridgeContext, BridgeServices, BridgeControls } from './types';
import { emit } from '../custom-ui/bridge/methods/events';

export const useBridgeContext = (
	props: BridgeContextInput,
	getServices: () => BridgeServices,
	contextChangeListeners?: ReadonlyArray<ChangeListener>,
): {
	destroy: () => void;
	getBridgeContext: () => BridgeContext;
	onChange: (listener: ChangeListener) => () => void;
	setBridgeControl: (controls: BridgeControls) => void;
} => {
	// Created once for the lifetime of the component.
	// BridgeContextObject owns all memoization and change notification internally.
	const contextObjectRef = useRef<BridgeContextObject | null>(null);
	if (contextObjectRef.current === null) {
		contextObjectRef.current = new BridgeContextObject(props, getServices);
	}

	// Update after every commit. Bridge calls arrive via postRobot (async message events)
	// so the context will always be current before any bridge method is invoked.
	useEffect(() => {
		contextObjectRef.current!.update(props);
	});

	const getBridgeContext = useCallbackOne(() => contextObjectRef.current!.get(), []);
	const destroy = useCallbackOne(() => contextObjectRef.current!.destroy(), []);
	const onChange = useCallbackOne(
		(listener: ChangeListener) => contextObjectRef.current!.onChange(listener),
		[],
	);
	const setBridgeControl = useCallbackOne(
		(controls: BridgeControls) => contextObjectRef.current!.setBridgeControl(controls),
		[],
	);

	// This registers general bridge context change events that is common to all
	// bridge runtimes and Forge modules.
	useEffect(() => {
		return onChange((_prev, next, changes) => {
			if (changes.theme || changes.surfaceColor) {
				emit('FORGE_CORE_THEME_CHANGED', { theme: next.theme });
			}
		});
	}, [onChange]);

	// Register integrator-supplied context change listeners (e.g. from xen-editor-provider).
	// Listeners must be stable module-level references to avoid re-registration on each render.
	useEffect(() => {
		if (!contextChangeListeners?.length) return;
		const unsubs = contextChangeListeners.map((listener) => onChange(listener));
		return () => unsubs.forEach((unsub) => unsub());
	}, [onChange, contextChangeListeners]);

	return { getBridgeContext, destroy, onChange, setBridgeControl };
};
